import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { Stack } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';

import { CategorySelector } from '@confluence/category-selector';
import { DebouncedSearchBar } from '@confluence/debounced-search-bar';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	SNIPPET_CATEGORY_IDS,
	useSnippetFilterCategories,
} from '../constants/snippetCategories.ts';
import { useSnippets } from '../store/snippetsStore.ts';
import { useSnippetFilters } from '../hooks/useSnippetFilters.ts';

import { SnippetPanelList } from './SnippetPanelList.tsx';
import { SnippetPanelSkeleton } from './skeleton/SnippetPanelSkeleton.tsx';

const messages = defineMessages({
	snippetsRecommended: {
		id: 'snippets.panel.recommended',
		defaultMessage: 'Recommended',
		description: 'Header for the list of Block Templates within the right panel',
	},
	searchSnippetsPlaceholderText: {
		id: 'snippets.panel.search.placeholderText',
		defaultMessage: 'Search author, category, topic',
		description: 'The placeholder text in the templates search box, visible when it is empty',
	},
});

export const SnippetPanel = () => {
	const { userId } = useSessionData();
	const intl = useIntl();

	const [{ isLoading, snippets }] = useSnippets();

	const { selectedCategoryId, searchQuery, filteredSnippets, setSearchQuery, setCategoryFilter } =
		useSnippetFilters(snippets, userId);

	const categories = useSnippetFilterCategories();

	const shouldShowRecommended =
		selectedCategoryId === SNIPPET_CATEGORY_IDS.ALL && searchQuery === '';

	if (isLoading) {
		return <SnippetPanelSkeleton />;
	}

	return (
		<Stack space="space.200" grow="fill">
			<DebouncedSearchBar
				placeholder={intl.formatMessage(messages.searchSnippetsPlaceholderText)}
				isCompact
				onChange={(query) => setSearchQuery(query)}
				initialValue={searchQuery || ''}
			/>
			<CategorySelector
				categories={categories}
				selectedCategoryIds={[selectedCategoryId]}
				onCategorySelected={(category) => setCategoryFilter(category.id as SNIPPET_CATEGORY_IDS)}
			/>
			<SnippetPanelList snippets={filteredSnippets} />
			{shouldShowRecommended && (
				<Heading size="xxsmall">
					<FormattedMessage {...messages.snippetsRecommended} />
				</Heading>
			)}
		</Stack>
	);
};
